/**
 * AIAnalysis Service
 *
 * This service connects data sources to the AIAnalysis module
 * and handles the generation of prompts for AI processing.
 */

import { generateSummaryPrompt } from '../prompts';
import { AIChartSeries } from '../../../KSExport/reportNTParser/aiAnalysis';
import { getAIAnalysis, ReportViewModel } from '@hone-automation/common';

/**
 * Interface for the data required by the summary prompt
 */
export interface SummaryPromptData {
  data: any[];
  aiChartMetrics: AIChartSeries[];
  statistics: any;
  significantChanges: any[];
  username: string;
  extendedContext?: { location?: string; user?: string; userName?: string };
  period: string;
}

// Define a more generic type for reports to handle type compatibility
type GenericReport = any;

/**
 * Generate a summary prompt directly from pre-processed AI analysis data
 * @param aiAnalysisData The pre-processed AI analysis data
 * @param username The name of the user/restaurant owner
 * @returns The formatted prompt ready for AI processing
 */
export async function generateSummaryPromptFromAIAnalysisData(
  aiAnalysisData: any,
  username: string = 'User',
  extendedContext?: { location?: string; user?: string; userName?: string },
  cacheDate?: string
): Promise<string> {
  // Convert the data object to an array format expected by generateSummaryPrompt if needed
  const dataArray = aiAnalysisData.data.hierarchicalData
    ? [aiAnalysisData.data.hierarchicalData]
    : Array.isArray(aiAnalysisData.data)
      ? aiAnalysisData.data
      : [aiAnalysisData.data];

  const aiChartMetrics = aiAnalysisData.data.aiChartMetrics;

  // Convert significantChanges to array if it's not already
  const significantChangesArray = Array.isArray(aiAnalysisData.significantChanges)
    ? aiAnalysisData.significantChanges
    : Object.values(aiAnalysisData.significantChanges);

  // Generate the prompt using our template and the data
  return generateSummaryPrompt(
    dataArray,
    aiChartMetrics,
    aiAnalysisData.statistics,
    significantChangesArray,
    username,
    aiAnalysisData, // Pass the raw aiAnalysis data
    cacheDate, // Pass the cache date
    extendedContext // Pass the extended context
  );
}

/**
 * Generate a summary prompt from pre-transformed data
 * @param promptData The already transformed data
 * @param username The name of the user/restaurant owner
 * @returns The formatted prompt ready for AI processing
 */
export async function generateSummaryPromptFromReportViewModel(
  promptData: SummaryPromptData,
  username: string = 'User'
): Promise<string> {
  // Generate the prompt using our template and the provided data
  return generateSummaryPrompt(
    promptData.data,
    promptData.aiChartMetrics,
    promptData.statistics,
    promptData.significantChanges,
    username
  );
}

/**
 * Generate a summary prompt from KSExport ReportViewModel that includes AI analysis
 * @param reportData The transformed data from KSExport
 * @param username The name of the user/restaurant owner
 * @returns The formatted prompt ready for AI processing
 */
export async function generateSummaryPromptFromKSExportData(
  reportData: ReportViewModel,
  username: string = 'User'
): Promise<string> {
  // If the reportData already has AI analysis data, use it (v1 and v2 compatible)
  const aiAnalysis = getAIAnalysis(reportData);
  if (aiAnalysis) {
    return generateSummaryPromptFromAIAnalysisData(aiAnalysis, username);
  }

  // Otherwise, we need to generate the AI analysis data from the raw report
  throw new Error(
    'ReportViewModel does not contain AI analysis data. The extendedAnalysis flag must be set to true when parsing the report.'
  );
}
